import React, {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
	useEffect,
} from "react";
import { TTab, VoiceTypes } from "../../utils";
import { useSelector } from "react-redux";
import { IRootState } from "../../redux/store";

interface Voice {
	voice_id: string;
	name: string;
}

interface MediaPreviewContextProps {
	videoUrl: string;
	currentTab: TTab | null;
	imageUrl: string;
	videoState: 'not active' | 'playing' | 'paused';
    currentVoiceId: string;
	isLoadingMedia: boolean;
	setIsLoadingMedia: (value: boolean) => void;
	setCurrentTab: (value: TTab | null) => void;
    setVideoState: (value: 'not active' | 'playing' | 'paused') => void;
    setCurrentVoiceId: (value: string) => void;
	play: () => void;
	pause: () => void;
	videoRef: React.RefObject<HTMLVideoElement>;
}

const MediaPreviewContext = createContext<MediaPreviewContextProps | null>(null);

export const MediaPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [videoUrl, setVideoUrl] = useState<string>("");
	const [imageUrl, setImageUrl] = useState<string>("");
	const [currentTab, setCurrentTab] = useState<TTab | null>("appearance");
    const formData = useSelector((state: IRootState) => state.avatarForm);
	const [videoState, setVideoState] = useState<'not active' | 'playing' | 'paused'>('not active');
    const [currentVoiceId, setCurrentVoiceId] = useState<string>(formData.voice);
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isLoadingMedia, setIsLoadingMedia] = useState(false);
    const availableVoices: VoiceTypes[] | [] = useSelector((state: IRootState) => state.voicesList);
	const videoCache = useRef<Record<string, string>>({});
	const imageCache = useRef<Record<string, string>>({});

	const loadVideo = async (voice: Voice, avatarType: string) => {
		setIsLoadingMedia(true);
		const videoKey = `${avatarType.split("/")[1]}_${voice.name.toLowerCase()}`;
		if (videoCache.current[videoKey]) {
			setVideoUrl(videoCache.current[videoKey]);
			setIsLoadingMedia(false);
			return;
		}

		try {
			const videoModule = await import(`../../assets/video/avatar_voices/${videoKey}.mp4`);
			videoCache.current[videoKey] = videoModule.default;
			setVideoUrl(videoModule.default);
		} catch {
			setVideoUrl("");
		} finally {
			setIsLoadingMedia(false);
		}
	};

	const loadImage = async (scene: string, avatarType: string) => {
		setIsLoadingMedia(true);
		const imageKey = `${avatarType.split("/")[1]}_${scene.split("/")[1]}`;

		if (imageCache.current[imageKey]) {
			setImageUrl(imageCache.current[imageKey]);
			setIsLoadingMedia(false);
			return;
		}

		try {
			const imageModule = await import(`../../assets/pictures/avatars_in_scene/${imageKey}.png`);
			imageCache.current[imageKey] = imageModule.default;
			setImageUrl(imageModule.default);
		} catch {
			setImageUrl("");
		} finally {
			setIsLoadingMedia(false);
		}
	};

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;

        const onEnded = () => {
            setVideoState('not active');
        };

		const onLoaded = () => {
			if (videoState === 'playing') {
				el.play();
			}
		};

		el.addEventListener('loadeddata', onLoaded);
        el.addEventListener('ended', onEnded);

        return () => {
            el.removeEventListener('ended', onEnded);
			el.removeEventListener('loadeddata', onLoaded);
        };
    }, [videoRef, videoUrl]);

	useEffect(() => {
        setCurrentVoiceId(formData.voice);
        const currentVoice = availableVoices.find((v) => v.voice_id === formData.voice);
        if (currentVoice) {
            loadVideo(currentVoice, formData.type);
        }
		loadImage(formData.scene, formData.type);
    }, [formData.voice, formData.scene]);

    const play = useCallback(() => {
		if (videoRef.current) {
			videoRef.current.play();
			setVideoState("playing");
		}
    }, [videoRef]);

	const pause = useCallback(() => {
		if (videoRef.current) {
			videoRef.current.pause();
			setVideoState("paused");
		}
	}, [videoRef]);

	return (
		<MediaPreviewContext.Provider
			value={{
				videoUrl,
				currentTab,
				imageUrl,
                videoState,
                currentVoiceId,
				isLoadingMedia,
				setIsLoadingMedia,
				setCurrentTab,
                setCurrentVoiceId,
                setVideoState,
				play,
				pause,
				videoRef,
			}}
		>
			{children}
		</MediaPreviewContext.Provider>
	);
};

export const useMediaPreview = () => {
	const context = useContext(MediaPreviewContext);
	if (!context) {
		throw new Error("useMediaPreview must be used within a MediaPreviewProvider");
	}
	return context;
};
